/**
 * Browsers' MP3 decoders don't agree on where a frame's sound starts.
 * FFmpeg's (Chrome, Firefox) keeps the decoder's own 529-sample delay in
 * what it outputs; Apple's (Safari) takes it out but returns as many
 * samples, so its sound comes that much early. A tiny MP3 with one click at
 * a known sample is decoded once, and the difference is used when MP3 sound
 * is placed. Without it, an MP3 recording lined up in Safari was 12 ms off
 * at 44.1 kHz (24 ms at 22.05 kHz).
 */

import type { StreamDecoder } from './audioDecode';

/** 0.25 s at 22.05 kHz, 32 kb/s CBR, no tags: silence with a click at sample 2205. */
const CLICK_MP3 = '//NAxAAAAANIAAAAAExBTUUzLjEwMVVVVVVVVVVVVVVMQU1FMy4xMDFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVX/80LEWwAAA0gAAAAAVVVVVVVVVVVVVVVVVVVVVVVVVVXwYM8HBvBQf7gYChv3AwFFve7gEAoeAL93uAokAwREv73e7gMEQoBgsIBL/777hMBAoBwEGAOAgh//33/gcAA4BwAFAcCDg0D/80DEpAAAA0gAAAAAgKG///+2zWawICgwIBQMGAUDCoAgMHQBAYR///7Wa3//AUJCIChILSCKEhVUo2oCdUmbUBar56qZS0eUr/lK/5SvzPKUxjGN1QxjfmM/5jP0NMZ+Y0xn5jTGfv/zQsSjAAADSAFAAABjMYz8xmMZ+YCZDPaYCZDbTATCK4Ghj5YOcGnZYOcRKkxBTUUzLjEwMaqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqv/zQMT/G/PauAGFKACqqqqqqqqqqqqqqqqqqqqqqqqqTEFNRTMuMTAxqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//NCxOoUio6RccMQAKqqqqqqqqqqqqqqqqqqqqqqqqqqTEFNRTMuMTAxqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//NAxKQAAANIAAAAAKqqqqqqqqqqqqqqqqqqqqqqqqpMQU1FMy4xMDGqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqr/80LEowAAA0gAAAAAqqqqqqqqqqqqqqqqqqqqqqqqqqpMQU1FMy4xMDGqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqr/80DEpAAAA0gAAAAAqqqqqqqqqqqqqqqqqqqqqqqqqkxBTUUzLjEwMaqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqv/zQsSjAAADSAAAAACqqqqqqqqqqqqqqqqqqqqqqqqqqkxBTUUzLjEwMaqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqv/zQMSkAAADSAAAAACqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq//NCxKMAAANIAAAAAKqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq';
const CLICK_RATE = 22050;
/** Where FFmpeg's decode has the click: sample 2205, plus LAME's 576 and the decoder's 529 of delay. */
const FFMPEG_CLICK_AT = 3310;

let calibration: Promise<number> | null = null;

const fromBase64 = (text: string): Uint8Array => {
  const binary = atob(text);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
};

/**
 * Samples (at an MP3's own rate) by which this browser's decoder puts sound
 * earlier than FFmpeg's: about 529 in Safari, 0 elsewhere and when it can't
 * be measured. Measured once per page.
 */
export const mp3DecoderLeadSamples = (decoder: StreamDecoder): Promise<number> => {
  if (!calibration) {
    calibration = (async () => {
      try {
        const samples = await decoder(fromBase64(CLICK_MP3), CLICK_RATE);
        let peak = 0;
        let at = -1;
        for (let i = 0; i < samples.length; i += 1) {
          const value = Math.abs(samples[i]);
          if (value > peak) {
            peak = value;
            at = i;
          }
        }
        if (at < 0 || peak < 0.2) return 0;
        const lead = FFMPEG_CLICK_AT - at;
        // Only a decoder-delay-sized difference is believable.
        return Math.abs(lead) <= 1152 ? lead : 0;
      } catch {
        return 0;
      }
    })();
  }
  return calibration;
};

/** For tests: measure again next time. */
export const resetMp3Calibration = () => {
  calibration = null;
};
